import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import { AdSchema, SpeakableSchema } from "../components/SchemaMarkup";
import Breadcrumbs from "../components/Breadcrumbs";
import { Button } from "../components/ui/button";
import useIsTWA, { openExternal } from "../hooks/useIsTWA";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  MapPin,
  Eye,
  Clock,
  Phone,
  Mail,
  Share2,
  Heart,
  ChevronLeft,
  ChevronRight,
  Zap,
  Star,
  User,
  Flag,
  ArrowUp,
  Timer,
  Copy,
  MessageCircle,
  ZoomIn,
  Scale,
  DollarSign,
  ShieldCheck,
  Loader2
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ro } from "date-fns/locale";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

import ImageLightbox from "../components/ad-detail/ImageLightbox";
import SellerHeader from "../components/ad-detail/SellerHeader";
import AgeVerificationModal from "../components/AgeVerificationModal";

export default function AdDetailPage() {
  const { adId } = useParams();
  const navigate = useNavigate();
  const isTWA = useIsTWA();
  const { user } = useAuth();
  
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showContact, setShowContact] = useState(false);
  const [topupLoading, setTopupLoading] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [referralCount, setReferralCount] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  
  // Compare & Offer states
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [offerPrice, setOfferPrice] = useState("");
  const [offerCurrency, setOfferCurrency] = useState("RON");
  const [offerMessage, setOfferMessage] = useState("");
  const [submittingOffer, setSubmittingOffer] = useState(false);
  
  // Report states
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportReasons, setReportReasons] = useState([]);
  const [selectedReason, setSelectedReason] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [submittingReport, setSubmittingReport] = useState(false);
  const [sellerBadges, setSellerBadges] = useState([]);

  // Age-gate for 18+ category (escorts): show modal on deep-links unless user
  // already confirmed in the current browser session.
  const [showAgeModal, setShowAgeModal] = useState(false);
  // Iter 47 — Escrow buy flow
  const [showEscrowDialog, setShowEscrowDialog] = useState(false);
  const [escrowQuote, setEscrowQuote] = useState(null);
  const [escrowLoading, setEscrowLoading] = useState(false);
  useEffect(() => {
    if (!ad) return;
    if (ad.category_id !== "escorts") return;
    const confirmed = localStorage.getItem("age_verified_18plus") === "true";
    if (!confirmed) setShowAgeModal(true);
  }, [ad]);

  const fetchAd = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ads/${adId}`);
      setAd(response.data);
      // Fetch seller badges
      if (response.data.user_id) {
        try {
          const badgesRes = await axios.get(`${API_URL}/api/users/${response.data.user_id}/badges`);
          setSellerBadges(badgesRes.data.badges?.map(b => b.id) || []);
        } catch { setSellerBadges([]); }
      }
    } catch (error) {
      console.error("Error fetching ad:", error);
      toast.error("Anunțul nu a fost găsit");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [adId, navigate]);

  const fetchReferralCode = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/referral-code`, {
        withCredentials: true
      });
      setReferralCode(response.data.referral_code);
      setReferralCount(response.data.referral_count || 0);
    } catch (error) {
      console.error("Error fetching referral code:", error);
    }
  }, []);

  useEffect(() => {
    fetchAd();
    if (user) {
      fetchReferralCode();
    }
  }, [user, fetchAd, fetchReferralCode]);

  const fetchReportReasons = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/report/reasons`);
      setReportReasons(response.data.reasons);
    } catch (error) {
      console.error("Error fetching report reasons:", error);
    }
  };

  const handleOpenReport = () => {
    if (reportReasons.length === 0) {
      fetchReportReasons();
    }
    setShowReportDialog(true);
  };

  const handleSubmitReport = async () => {
    if (!selectedReason) {
      toast.error("Selectează un motiv pentru raportare");
      return;
    }
    
    setSubmittingReport(true);
    try {
      await axios.post(
        `${API_URL}/api/ads/${adId}/report`,
        { 
          reason: selectedReason, 
          description: reportDescription 
        },
        { withCredentials: true }
      );
      toast.success("Mulțumim! Raportul tău a fost trimis și va fi analizat.");
      setShowReportDialog(false);
      setSelectedReason("");
      setReportDescription("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la trimiterea raportului");
    } finally {
      setSubmittingReport(false);
    }
  };

  const handleTopup = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    setTopupLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/ads/${adId}/topup`,
        {},
        { withCredentials: true }
      );
      toast.success("TopUp reușit! Anunțul tău este acum primul în categorie.");
      fetchAd();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la TopUp");
    } finally {
      setTopupLoading(false);
    }
  };

  const handleAutoTopupToggle = async (enabled) => {
    try {
      await axios.post(
        `${API_URL}/api/ads/${adId}/auto-topup`,
        { enabled },
        { withCredentials: true }
      );
      toast.success(`Auto-TopUp ${enabled ? "activat" : "dezactivat"}`);
      fetchAd();
    } catch (error) {
      toast.error("Eroare la actualizare");
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
    toast.success("Link de referral copiat! Distribuie-l pentru TopUp mai rapid.");
  };

  const [isFavorite, setIsFavorite] = useState(false);

  const checkFavorite = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/favorites/check/${adId}`, {
        withCredentials: true
      });
      setIsFavorite(response.data.is_favorite);
    } catch (error) {
      console.error("Error checking favorite:", error);
    }
  }, [adId]);

  useEffect(() => {
    if (user && ad) {
      checkFavorite();
    }
  }, [user, ad, checkFavorite]);

  const toggleFavorite = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    try {
      if (isFavorite) {
        await axios.delete(`${API_URL}/api/favorites/${adId}`, { withCredentials: true });
        toast.success("Eliminat din favorite");
      } else {
        await axios.post(`${API_URL}/api/favorites/${adId}`, {}, { withCredentials: true });
        toast.success("Adăugat la favorite");
      }
      setIsFavorite(!isFavorite);
    } catch (error) {
      toast.error("Eroare la actualizare");
    }
  };

  const startConversation = () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    if (ad.user_id === user.user_id) {
      toast.error("Nu poți trimite mesaj la propriul anunț");
      return;
    }
    navigate(`/messages?ad=${adId}&to=${ad.user_id}`);
  };

  // Iter 47 — Escrow buy flow
  const openEscrowDialog = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    if (ad.user_id === user.user_id) {
      toast.error("Nu poți cumpăra propriul anunț");
      return;
    }
    // Iter 52 — Play Store Organization-policy compliance: when running inside
    // the Android TWA we MUST NOT process payments in-app. Bounce the user to
    // production Chrome where the escrow checkout completes externally.
    if (isTWA) {
      openExternal(`/ad/${adId}?action=escrow`);
      return;
    }
    setEscrowLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/escrow/quote/${adId}`);
      setEscrowQuote(res.data);
      setShowEscrowDialog(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Plată sigură indisponibilă pentru acest anunț");
    } finally {
      setEscrowLoading(false);
    }
  };

  const confirmEscrowBuy = async () => {
    setEscrowLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/escrow/create`,
        { ad_id: adId },
        { withCredentials: true }
      );
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else if (res.data?.fully_covered_by_deposit) {
        toast.success(res.data.message || "Tranzacție creată");
        navigate("/escrow");
      } else {
        navigate("/escrow");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la inițierea plății");
    } finally {
      setEscrowLoading(false);
    }
  };

  // Add to comparison
  const handleAddToCompare = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/compare/add`,
        { ad_id: adId },
        { withCredentials: true }
      );
      toast.success(`Adăugat la comparație (${response.data.count}/4)`);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Eroare la adăugare");
      }
    }
  };

  // Submit offer
  const handleSubmitOffer = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    
    const price = parseFloat(offerPrice);
    if (!price || price <= 0) {
      toast.error("Introdu un preț valid");
      return;
    }
    
    setSubmittingOffer(true);
    try {
      await axios.post(
        `${API_URL}/api/offers`,
        {
          ad_id: adId,
          offered_price: price,
          currency: offerCurrency,
          message: offerMessage || null
        },
        { withCredentials: true }
      );
      toast.success("Oferta a fost trimisă!");
      setShowOfferDialog(false);
      setOfferPrice("");
      setOfferCurrency(ad?.currency || "RON");
      setOfferMessage("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la trimiterea ofertei");
    } finally {
      setSubmittingOffer(false);
    }
  };

  const handlePaidTopup = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    // Iter 52 — Play Store compliance: open paid topup in external Chrome
    if (isTWA) {
      openExternal(`/ad/${adId}?action=topup`);
      return;
    }
    setTopupLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/ads/${adId}/topup-paid`,
        {},
        { withCredentials: true }
      );
      // Redirect to Viva Wallet checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la procesarea plății");
      setTopupLoading(false);
    }
  };

  const handleBoost = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    if (isTWA) {
      openExternal(`/ad/${adId}?action=boost`);
      return;
    }
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/create-order`,
        {
          ad_id: adId,
          payment_type: "boost",
          customer_email: user.email,
          customer_name: user.name
        },
        { withCredentials: true }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error("Eroare la procesarea plății");
    }
  };

  const handlePromote = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    if (isTWA) {
      openExternal(`/ad/${adId}?action=promote`);
      return;
    }
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/create-order`,
        {
          ad_id: adId,
          payment_type: "promote",
          customer_email: user.email,
          customer_name: user.name
        },
        { withCredentials: true }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error("Eroare la procesarea plății");
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copiat în clipboard!");
  };

  const formatPrice = () => {
    if (!ad) return "";
    const sym = (ad.currency || "RON") === "EUR" ? "€" : "RON";
    if (ad.price_type === "free") return "Gratuit";
    if (ad.price_type === "negotiable") return ad.price ? `${ad.price.toLocaleString("ro-RO")} ${sym} (neg.)` : "Negociabil";
    return ad.price ? `${ad.price.toLocaleString("ro-RO")} ${sym}` : "Preț la cerere";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="aspect-video rounded-2xl bg-[#0A0A0A] mb-8" />
            <div className="h-8 bg-[#0A0A0A] rounded w-3/4 mb-4" />
            <div className="h-6 bg-[#0A0A0A] rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (!ad) return null;

  const images = ad.images?.length > 0 ? ad.images : [
    "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop"
  ];

  const timeAgo = ad.created_at 
    ? formatDistanceToNow(new Date(ad.created_at), { addSuffix: true, locale: ro })
    : "";

  const isOwner = user && user.user_id === ad.user_id;

  // Generate SEO data for ad
  const adSeoTitle = ad ? `${ad.title} - ${ad.price ? ad.price + ' ' + ((ad.currency || 'RON') === 'EUR' ? '€' : 'RON') : 'Preț negociabil'} | X67` : 'Anunț | X67 Digital';
  const adSeoDescription = ad ? `${ad.description?.slice(0, 150)}... ${ad.city_name || ad.localitate || ''} - ${ad.category_name || ''}` : 'Anunț pe X67 Digital Media';
  const adSeoImage = ad?.images?.[0] || 'https://x67digital.com/og-image.png';

  return (
    <div className="min-h-screen bg-[#050505] relative" data-testid="ad-detail-page">
      {/* Premium background halo */}
      <div className="absolute inset-0 -z-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -left-32 w-[600px] h-[600px] rounded-full bg-blue-500/10 blur-[140px]" />
        <div className="absolute top-40 -right-40 w-[500px] h-[500px] rounded-full bg-fuchsia-500/10 blur-[140px]" />
      </div>

      {/* Image Lightbox */}
      {showLightbox && images.length > 0 && (
        <ImageLightbox 
          images={images}
          currentIndex={currentImageIndex}
          onClose={() => setShowLightbox(false)}
          onNext={() => setCurrentImageIndex(i => (i + 1) % images.length)}
          onPrev={() => setCurrentImageIndex(i => (i - 1 + images.length) % images.length)}
          setCurrentImageIndex={setCurrentImageIndex}
        />
      )}
      
      <SEOHead 
        title={adSeoTitle}
        description={adSeoDescription}
        keywords={`${ad?.title || ''}, ${ad?.category_name || ''}, ${ad?.city_name || ''}, anunturi romania`}
        image={adSeoImage}
        url={`https://x67digital.com/ad/${adId}`}
        type="product"
      />
      <AdSchema ad={ad} />
      <SpeakableSchema cssSelectors={["[data-testid='ad-title']", "[data-testid='ad-price']", "[data-testid='ad-description']"]} />
      <Header />

      {/* Age-gate for 18+ content (escorts) — shown only for deep links to adult ads */}
      <AgeVerificationModal
        isOpen={showAgeModal}
        onConfirm={() => setShowAgeModal(false)}
        onCancel={() => setShowAgeModal(false)}
      />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Breadcrumb */}
        <Breadcrumbs 
          items={[
            { label: ad.category_name || 'Categorie', href: `/category/${ad.category_id}` },
            { label: ad.title }
          ]} 
          className="mb-6"
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images & Details */}
          <div className="lg:col-span-2 space-y-5 sm:space-y-6">
            {/* Image Gallery */}
            <div className="relative rounded-2xl overflow-hidden bg-gradient-to-b from-[#0e0e12] to-[#0a0a0a] border border-white/10 shadow-2xl shadow-black/40">
              {/* Badges */}
              <div className="absolute top-4 left-4 z-20 flex gap-2">
                {ad.is_promoted && (
                  <span className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-xs font-bold shadow-lg shadow-blue-500/30">
                    <Star className="w-3.5 h-3.5" />
                    Promovat
                  </span>
                )}
                {ad.is_boosted && (
                  <span className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white text-xs font-bold shadow-lg shadow-fuchsia-500/30">
                    <Zap className="w-3.5 h-3.5" />
                    Ridicat
                  </span>
                )}
              </div>

              {/* Main Image */}
              <div 
                className="aspect-video relative group cursor-pointer"
                onClick={() => setShowLightbox(true)}
                onTouchStart={(e) => { e.currentTarget.dataset.touchX = e.touches[0].clientX; }}
                onTouchEnd={(e) => {
                  const startX = parseFloat(e.currentTarget.dataset.touchX || 0);
                  const endX = e.changedTouches[0].clientX;
                  const diff = startX - endX;
                  if (Math.abs(diff) > 50) {
                    if (diff > 0) setCurrentImageIndex(i => (i + 1) % images.length);
                    else setCurrentImageIndex(i => (i - 1 + images.length) % images.length);
                  }
                }}
              >
                <img
                  src={images[currentImageIndex]}
                  alt={ad.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop";
                  }}
                />
                
                {/* Zoom indicator */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 backdrop-blur-sm rounded-full p-3">
                    <ZoomIn className="w-6 h-6 text-white" />
                  </div>
                </div>
                
                {/* Navigation */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(i => (i - 1 + images.length) % images.length); }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                      data-testid="prev-image-btn"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(i => (i + 1) % images.length); }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                      data-testid="next-image-btn"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="flex gap-2 p-4 overflow-x-auto">
                  {images.map((img, index) => (
                    <button
                      key={img}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                        index === currentImageIndex ? "border-blue-500" : "border-transparent"
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Title & Price */}
            <div className="relative rounded-2xl bg-gradient-to-br from-white/[0.06] via-white/[0.03] to-white/[0.02] border border-white/10 backdrop-blur-md p-5 sm:p-6 shadow-xl shadow-black/40 overflow-hidden">
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-5 mb-4">
                  <h1 className="text-xl sm:text-2xl md:text-3xl font-black text-white break-words leading-tight tracking-tight" data-testid="ad-title">
                    {ad.title}
                  </h1>
                  <div className="flex-shrink-0">
                    <span 
                      className={`block text-2xl sm:text-3xl md:text-4xl font-black leading-none ${
                        ad.price_type === "free"
                          ? "text-emerald-400"
                          : "bg-gradient-to-r from-emerald-300 via-blue-300 to-fuchsia-300 bg-clip-text text-transparent"
                      }`}
                      data-testid="ad-price"
                    >
                      {formatPrice()}
                    </span>
                    {ad.price_type === "negotiable" && (
                      <span className="block text-xs text-amber-400 font-semibold mt-1 uppercase tracking-wider">Negociabil</span>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-300 text-xs font-medium">
                    <MapPin className="w-3.5 h-3.5 text-rose-400" />
                    {ad.city_name || ad.localitate}{ad.city_county && `, ${ad.city_county}`}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-300 text-xs font-medium tabular-nums">
                    <Eye className="w-3.5 h-3.5 text-blue-400" />
                    {ad.views} vizualizări
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-300 text-xs font-medium">
                    <Clock className="w-3.5 h-3.5 text-emerald-400" />
                    {timeAgo}
                  </span>
                  {ad.category_name && (
                    <span 
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border"
                      style={{ 
                        backgroundColor: `${ad.category_color}15`,
                        color: ad.category_color,
                        borderColor: `${ad.category_color}40`
                      }}
                    >
                      {ad.category_name}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="rounded-2xl bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/10 p-5 sm:p-6 backdrop-blur-md">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <span className="inline-block w-1 h-5 rounded-full bg-gradient-to-b from-blue-500 to-fuchsia-500" />
                Descriere
              </h2>
              <p className="text-slate-300 whitespace-pre-wrap leading-relaxed" data-testid="ad-description">
                {ad.description}
              </p>
            </div>

            {/* Details */}
            {ad.details && Object.keys(ad.details).length > 0 && (
              <div className="rounded-2xl bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/10 p-5 sm:p-6 backdrop-blur-md">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <span className="inline-block w-1 h-5 rounded-full bg-gradient-to-b from-emerald-500 to-cyan-500" />
                  Detalii
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {Object.entries(ad.details).map(([key, value]) => (
                    <div key={key} className="flex justify-between gap-3 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/5">
                      <span className="text-slate-400 capitalize text-sm">{key.replace(/_/g, " ")}</span>
                      <span className="text-white text-sm font-semibold text-right">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Contact & Actions */}
          <div className="space-y-6">
            {/* Contact Card */}
            <div className="relative rounded-2xl bg-gradient-to-br from-white/[0.06] via-white/[0.03] to-white/[0.02] border border-white/10 backdrop-blur-md p-5 sm:p-6 shadow-2xl shadow-black/40 sticky top-24 overflow-hidden">
              <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
              <div className="relative">
              {/* Seller Info */}
              <SellerHeader ad={ad} sellerBadges={sellerBadges} />
              
              {/* View Seller Profile */}
              <Link
                to={`/seller/${ad.user_id}`}
                className="flex items-center justify-center gap-1.5 w-full text-center py-2 mb-4 text-xs font-semibold text-blue-300 hover:text-blue-200 hover:bg-white/5 rounded-lg transition-all"
              >
                Vezi profilul vânzătorului
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>

              {/* Contact Buttons */}
              {showContact ? (
                <div className="space-y-2">
                  {ad.contact_phone && (
                    <a 
                      href={`tel:${ad.contact_phone}`}
                      className="flex items-center gap-3 w-full p-4 rounded-xl bg-gradient-to-r from-emerald-600/20 to-emerald-600/10 border border-emerald-500/30 text-emerald-300 hover:from-emerald-600/30 hover:to-emerald-600/20 transition-all group"
                      data-testid="contact-phone"
                    >
                      <Phone className="w-5 h-5 group-hover:scale-110 transition-transform" />
                      <span className="font-bold">{ad.contact_phone}</span>
                    </a>
                  )}
                  {ad.contact_email && (
                    <a 
                      href={`mailto:${ad.contact_email}`}
                      className="flex items-center gap-3 w-full p-4 rounded-xl bg-gradient-to-r from-blue-600/20 to-blue-600/10 border border-blue-500/30 text-blue-300 hover:from-blue-600/30 hover:to-blue-600/20 transition-all"
                      data-testid="contact-email"
                    >
                      <Mail className="w-5 h-5" />
                      <span className="truncate font-bold">{ad.contact_email}</span>
                    </a>
                  )}
                </div>
              ) : (
                <Button
                  className="w-full h-14 text-base rounded-xl bg-gradient-to-r from-blue-600 via-blue-500 to-fuchsia-600 hover:from-blue-500 hover:to-fuchsia-500 text-white font-bold shadow-lg shadow-blue-500/40 hover:shadow-blue-500/60 transition-all"
                  onClick={() => setShowContact(true)}
                  data-testid="show-contact-btn"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Arată contactul
                </Button>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2 mt-3">
                <Button
                  variant="outline"
                  className="border-white/10 bg-white/[0.02] text-slate-300 hover:text-white hover:bg-white/5"
                  onClick={handleShare}
                  data-testid="share-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Distribuie
                </Button>
                <Button
                  variant="outline"
                  className={`border-white/10 bg-white/[0.02] ${isFavorite ? "text-rose-300 border-rose-500/40 bg-rose-500/10" : "text-slate-300"} hover:text-rose-300 hover:bg-rose-500/10 hover:border-rose-500/30`}
                  onClick={toggleFavorite}
                  data-testid="favorite-btn"
                >
                  <Heart className={`w-4 h-4 mr-2 ${isFavorite ? "fill-current" : ""}`} />
                  {isFavorite ? "Salvat" : "Salvează"}
                </Button>
              </div>

              {/* Shipping info (from seller custom rate — visible to buyer) */}
              {ad.shipping_rates && typeof ad.shipping_rates.rate === "number" && (
                <div className="mt-4 p-3 rounded-xl bg-blue-500/5 border border-blue-500/15 flex items-start gap-3" data-testid="ad-shipping-info">
                  <svg className="w-4 h-4 text-blue-300 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 17h14M5 17a2 2 0 01-2-2V9a2 2 0 012-2h5m9 10a2 2 0 002-2v-2l-4-5h-5m0 9V7" />
                  </svg>
                  <div className="text-xs text-blue-100/90 flex-1">
                    <div className="font-semibold text-blue-200">
                      {ad.shipping_rates.carrier || "Transport"} —{" "}
                      {ad.shipping_rates.rate === 0 ? (
                        <span className="text-emerald-300">Gratuit</span>
                      ) : (
                        <span>{ad.shipping_rates.rate} {ad.shipping_rates.currency || "RON"}</span>
                      )}
                    </div>
                    {ad.shipping_rates.free_over ? (
                      <div className="text-blue-200/70 mt-0.5">
                        Livrare gratuită la comenzi peste {ad.shipping_rates.free_over} {ad.shipping_rates.currency || "RON"}
                      </div>
                    ) : null}
                  </div>
                </div>
              )}

              {/* Iter 47 — Cumpără cu Escrow (only physical goods with fixed price) */}
              {!isOwner && ad.price > 0 && ad.price_type !== "free" && ad.category_id !== "escorts" && (
                <Button
                  className="w-full mt-3 h-12 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 hover:from-amber-400 hover:via-orange-400 hover:to-rose-400 text-white font-bold shadow-lg shadow-amber-500/30 ring-1 ring-amber-300/30"
                  onClick={openEscrowDialog}
                  disabled={escrowLoading}
                  data-testid="escrow-buy-btn"
                >
                  {escrowLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ShieldCheck className="w-4 h-4 mr-2" />
                  )}
                  Cumpără cu Plată Sigură
                </Button>
              )}

              {/* Message Button (not for owner) */}
              {!isOwner && (
                <Button
                  className="w-full mt-3 h-11 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold shadow-lg shadow-emerald-500/30"
                  onClick={startConversation}
                  data-testid="message-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Trimite mesaj
                </Button>
              )}
              
              {/* Compare & Offer Buttons (not for owner) */}
              {!isOwner && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Button
                    variant="outline"
                    className="border-white/10 bg-white/[0.02] text-slate-300 hover:text-white hover:bg-white/5"
                    onClick={handleAddToCompare}
                    data-testid="compare-btn"
                  >
                    <Scale className="w-4 h-4 mr-2" />
                    Compară
                  </Button>
                  {ad.price > 0 && ad.price_type !== "free" && (
                    <Button
                      variant="outline"
                      className="border-emerald-500/30 bg-emerald-500/5 text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-500/50"
                      onClick={() => setShowOfferDialog(true)}
                      data-testid="make-offer-btn"
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      Fă o ofertă
                    </Button>
                  )}
                </div>
              )}

              {/* Boost/Promote - TopUp Section */}
              {isOwner && ad.status === "active" && (
                <div className="mt-6 pt-6 border-t border-white/5 space-y-4">
                  {ad.category_id === "escorts" ? (
                    <>
                      <h4 className="text-white font-medium">TopUp Anunț Escorte - 10 RON</h4>
                      <p className="text-slate-400 text-xs">Plătește 10 RON pentru a ridica anunțul în TOP. Nelimitat pe zi!</p>
                      
                      {/* Paid TopUp Button for Escorts */}
                      <Button
                        className="w-full bg-gradient-to-r from-fuchsia-600 to-violet-600 hover:from-fuchsia-500 hover:to-violet-500 text-white shadow-[0_0_20px_rgba(217,70,239,0.3)]"
                        onClick={handlePaidTopup}
                        disabled={topupLoading}
                        data-testid="paid-topup-btn"
                      >
                        <Zap className="w-4 h-4 mr-2" />
                        {topupLoading ? "Se procesează..." : "💳 Top-Up 10 RON - Ridică în TOP"}
                      </Button>
                    </>
                  ) : (
                    <>
                      <h4 className="text-white font-medium">TopUp Anunț - GRATUIT</h4>
                      
                      {/* Free TopUp Button */}
                      <Button
                        className="w-full bg-gradient-to-r from-fuchsia-600 to-blue-600 hover:from-fuchsia-500 hover:to-blue-500 text-white"
                        onClick={handleTopup}
                        disabled={topupLoading}
                        data-testid="topup-btn"
                      >
                        <ArrowUp className="w-4 h-4 mr-2" />
                        {topupLoading ? "Se procesează..." : "TopUp - Ridică în TOP"}
                      </Button>
                    </>
                  )}
                  
                  {/* Auto TopUp Toggle */}
                  <div className="flex items-center justify-between p-4 rounded-xl bg-[#121212]">
                    <div className="flex items-center gap-3">
                      <Timer className="w-5 h-5 text-blue-400" />
                      <div>
                        <p className="text-white text-sm font-medium">Auto-TopUp</p>
                        <p className="text-slate-500 text-xs">Ridică automat la fiecare oră</p>
                      </div>
                    </div>
                    <Switch
                      checked={ad.auto_topup !== false}
                      onCheckedChange={handleAutoTopupToggle}
                    />
                  </div>
                  
                  {/* Referral Section */}
                  {referralCode && (
                    <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Share2 className="w-4 h-4 text-emerald-400" />
                        <p className="text-emerald-400 text-sm font-medium">Bonus Referral</p>
                      </div>
                      <p className="text-slate-400 text-xs mb-3">
                        {referralCount > 0 
                          ? `Ai ${referralCount} referral(uri)! TopUp la fiecare 40 min.`
                          : "Distribuie link-ul și vei putea face TopUp la fiecare 40 min în loc de 60!"}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                        onClick={copyReferralLink}
                      >
                        <Copy className="w-3 h-3 mr-2" />
                        Copiază link referral
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Report */}
              <button 
                onClick={handleOpenReport}
                className="flex items-center gap-2 mt-6 text-slate-500 hover:text-red-400 text-sm transition-colors"
                data-testid="report-ad-btn"
              >
                <Flag className="w-4 h-4" />
                Raportează anunțul
              </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      
      {/* Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-red-400" />
              Raportează Anunțul
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Selectează motivul pentru care dorești să raportezi acest anunț
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              {reportReasons.map((reason) => (
                <button
                  key={reason.id}
                  onClick={() => setSelectedReason(reason.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                    selectedReason === reason.id 
                      ? 'border-red-500/50 bg-red-500/10 text-white' 
                      : 'border-white/10 bg-[#121212] text-slate-400 hover:border-white/20'
                  }`}
                >
                  {reason.name}
                </button>
              ))}
            </div>
            
            <div>
              <label className="text-sm text-slate-400 mb-2 block">
                Detalii suplimentare (opțional)
              </label>
              <Textarea
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                placeholder="Descrie problema în detaliu..."
                className="bg-[#121212] border-white/10 text-white resize-none"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowReportDialog(false)}
              className="border-white/10"
            >
              Anulează
            </Button>
            <Button
              onClick={handleSubmitReport}
              disabled={!selectedReason || submittingReport}
              className="bg-red-600 hover:bg-red-500"
            >
              {submittingReport ? "Se trimite..." : "Trimite Raportul"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Offer Dialog */}
      <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Fă o Ofertă de Preț</DialogTitle>
            <DialogDescription className="text-slate-400">
              Propune un preț pentru "{ad?.title}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between text-sm mb-4 p-3 rounded-lg bg-white/5">
              <span className="text-slate-400">Prețul cerut:</span>
              <span className="text-white font-bold text-lg">
                {ad?.price?.toLocaleString()} {(ad?.currency || "RON") === "EUR" ? "€" : (ad?.currency || "RON")}
              </span>
            </div>
            
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Oferta ta *</label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={offerPrice}
                  onChange={(e) => setOfferPrice(e.target.value)}
                  placeholder="Introdu prețul oferit"
                  className="bg-[#121212] border-white/10 text-white text-lg flex-1"
                />
                <div className="flex rounded-lg overflow-hidden border border-white/10">
                  <button
                    type="button"
                    onClick={() => setOfferCurrency("EUR")}
                    className={`px-4 py-2 text-sm font-bold transition-all ${
                      offerCurrency === "EUR" 
                        ? "bg-emerald-500 text-white" 
                        : "bg-[#121212] text-slate-400 hover:text-white"
                    }`}
                  >
                    € EUR
                  </button>
                  <button
                    type="button"
                    onClick={() => setOfferCurrency("RON")}
                    className={`px-4 py-2 text-sm font-bold transition-all ${
                      offerCurrency === "RON" 
                        ? "bg-blue-500 text-white" 
                        : "bg-[#121212] text-slate-400 hover:text-white"
                    }`}
                  >
                    RON
                  </button>
                </div>
              </div>
              {offerPrice && ad?.price && offerCurrency === (ad?.currency || "RON") && (
                <p className="text-sm mt-2 text-slate-400">
                  {((parseFloat(offerPrice) / ad.price) * 100).toFixed(0)}% din prețul cerut
                  {parseFloat(offerPrice) < ad.price && (
                    <span className="text-green-400 ml-2">
                      (economisești {(ad.price - parseFloat(offerPrice)).toLocaleString()} {offerCurrency})
                    </span>
                  )}
                </p>
              )}
            </div>
            
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Mesaj (opțional)</label>
              <Textarea
                value={offerMessage}
                onChange={(e) => setOfferMessage(e.target.value)}
                placeholder="Adaugă un mesaj pentru vânzător..."
                className="bg-[#121212] border-white/10 text-white"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowOfferDialog(false)}>
              Anulează
            </Button>
            <Button 
              onClick={handleSubmitOffer}
              disabled={submittingOffer || !offerPrice}
              className="bg-green-600 hover:bg-green-500"
            >
              {submittingOffer ? "Se trimite..." : "Trimite Oferta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 47 — Escrow Buy Dialog */}
      <Dialog open={showEscrowDialog} onOpenChange={setShowEscrowDialog}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white sm:max-w-md" data-testid="escrow-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <span className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500 to-rose-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
                <ShieldCheck className="w-5 h-5 text-white" />
              </span>
              Plată sigură cu Escrow
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Banii sunt blocați la noi până confirmi că ai primit produsul. Vânzătorul îi primește doar după confirmarea ta.
            </DialogDescription>
          </DialogHeader>
          {escrowQuote && (
            <div className="space-y-3 mt-2">
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">Preț anunț</span>
                  <span className="text-white font-semibold tabular-nums" data-testid="escrow-amount">
                    {Number(escrowQuote.amount).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {escrowQuote.currency}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">
                    Comision X67 <span className="text-amber-300/80 text-xs">({escrowQuote.commission_rate_pct}% · min {escrowQuote.commission_min} {escrowQuote.currency})</span>
                  </span>
                  <span className="text-white font-semibold tabular-nums" data-testid="escrow-commission">
                    {Number(escrowQuote.commission).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {escrowQuote.currency}
                  </span>
                </div>
                <div className="h-px bg-white/10 my-2" />
                <div className="flex items-center justify-between">
                  <span className="text-white font-bold">Total de plată</span>
                  <span className="text-2xl font-black bg-gradient-to-r from-amber-300 via-orange-300 to-rose-300 bg-clip-text text-transparent tabular-nums" data-testid="escrow-total">
                    {Number(escrowQuote.total).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {escrowQuote.currency}
                  </span>
                </div>
              </div>
              <ul className="text-xs text-slate-400 space-y-1.5 px-1">
                <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">✓</span>Banii sunt blocați la X67, nu ajung la vânzător înainte să primești produsul</li>
                <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">✓</span>Ai la dispoziție 48h să plătești, apoi 8 zile să confirmi livrarea</li>
                <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">✓</span>Dispute deschisă oricând dacă apar probleme — admin mediază</li>
              </ul>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setShowEscrowDialog(false)}
              className="border-white/10 bg-transparent text-slate-300 hover:bg-white/5"
              data-testid="escrow-cancel-btn"
            >
              Renunță
            </Button>
            <Button
              onClick={confirmEscrowBuy}
              disabled={escrowLoading || !escrowQuote}
              className="bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 hover:from-amber-400 hover:via-orange-400 hover:to-rose-400 text-white font-bold shadow-lg shadow-amber-500/30"
              data-testid="escrow-confirm-btn"
            >
              {escrowLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Se procesează...</>
              ) : (
                <><ShieldCheck className="w-4 h-4 mr-2" />Plătește în siguranță</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
